/**
 * ReforgePanel -- Reroll an item affix at the reforge vendor.
 *
 * Select an item with affixes, pay gold, choose from 3 new affix options.
 *
 * @module ReforgePanel
 */

import focusManager from '../../input/FocusManager.js';
import townSystem from '../../systems/TownSystem.js';
import inventorySystem from '../../systems/InventorySystem.js';
import { RARITY_COLORS, RARITY_NAMES } from '../../systems/LootSystem.js';

export default class ReforgePanel {
  constructor(options = {}) {
    this._party = options.party || null;
    this._townId = options.townId || null;
    this._onClose = options.onClose || null;
    this._el = null;
    this._isOpen = false;
    this._statusMsg = '';
    this._selectedItem = null;
    this._reforgeOptions = [];
  }

  get isOpen() { return this._isOpen; }

  open() {
    if (this._isOpen) return;
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this.close();
    this._isOpen = true;

    this._el = document.createElement('div');
    this._el.className = 'town-panel-overlay';
    this._el.id = 'reforge-panel';
    this._el.setAttribute('role', 'dialog');
    this._el.setAttribute('aria-label', 'Reforge Anvil');
    this._el.setAttribute('data-scene', 'reforge');

    this._render();
    uiRoot.appendChild(this._el);
    focusManager.pushGroup('reforge', this._el);
  }

  close() {
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
      const topGroup = focusManager._currentGroup ? focusManager._currentGroup() : null;
      if (topGroup && topGroup.name === 'reforge') {
        focusManager.popGroup();
      }
    }
    this._el = null;
    this._isOpen = false;
    this._statusMsg = '';
    this._selectedItem = null;
    this._reforgeOptions = [];
    if (this._onClose) this._onClose();
  }

  _render() {
    if (!this._el) return;
    this._el.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'town-panel';

    // Header
    const header = document.createElement('div');
    header.className = 'town-panel-header';
    const title = document.createElement('h2');
    title.className = 'town-panel-title';
    title.textContent = 'Reforge Anvil';
    header.appendChild(title);

    const gold = document.createElement('span');
    gold.className = 'town-panel-gold';
    gold.textContent = `Gold: ${this._party?.gold || 0}`;
    header.appendChild(gold);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'town-panel-close';
    closeBtn.setAttribute('data-focusable', 'true');
    closeBtn.textContent = 'X';
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // Status
    if (this._statusMsg) {
      const status = document.createElement('div');
      status.className = 'town-panel-status';
      status.textContent = this._statusMsg;
      panel.appendChild(status);
    }

    const content = document.createElement('div');
    content.className = 'town-panel-content';

    if (this._selectedItem && this._reforgeOptions.length > 0) {
      this._renderReforgeChoices(content);
    } else {
      this._renderItemSelection(content);
    }

    panel.appendChild(content);
    this._el.appendChild(panel);
  }

  _renderItemSelection(container) {
    const desc = document.createElement('div');
    desc.className = 'town-panel-desc';
    desc.textContent = 'Select an item with affixes to reroll one of its properties.';
    container.appendChild(desc);

    const items = inventorySystem.getItems().filter(i => i.affixes && i.affixes.length > 0);

    if (items.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'town-panel-empty';
      empty.textContent = 'No items with affixes to reforge.';
      container.appendChild(empty);
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'town-item-grid';

    for (const item of items) {
      const cost = townSystem.getReforgeCost(item);
      const canAfford = (this._party?.gold || 0) >= cost;

      const card = document.createElement('div');
      card.className = `town-item-card${!canAfford ? ' town-item-card--disabled' : ''}`;
      card.setAttribute('data-focusable', 'true');

      const name = document.createElement('div');
      name.className = 'town-item-name';
      name.style.color = RARITY_COLORS[item.rarity] || RARITY_COLORS.normal;
      name.textContent = item.name;
      card.appendChild(name);

      const meta = document.createElement('div');
      meta.className = 'town-item-meta';
      meta.textContent = `${item.affixes.length} affixes | Cost: ${cost}g`;
      card.appendChild(meta);

      // Show current affixes
      for (const affix of item.affixes) {
        const affixEl = document.createElement('div');
        affixEl.className = 'town-item-affix';
        affixEl.textContent = affix.description || affix.name || '';
        card.appendChild(affixEl);
      }

      if (canAfford) {
        card.addEventListener('click', () => {
          this._selectedItem = item;
          this._reforgeOptions = townSystem.getReforgeOptions(item);
          this._render();
          focusManager.refreshGroup();
        });
      }

      grid.appendChild(card);
    }

    container.appendChild(grid);
  }

  _renderReforgeChoices(container) {
    const item = this._selectedItem;
    const cost = townSystem.getReforgeCost(item);

    const desc = document.createElement('div');
    desc.className = 'town-panel-desc';
    desc.textContent = `Reforging ${item.name} (${cost}g). Choose a new affix:`;
    container.appendChild(desc);

    const grid = document.createElement('div');
    grid.className = 'town-item-grid';

    for (let i = 0; i < this._reforgeOptions.length; i++) {
      const option = this._reforgeOptions[i];

      const card = document.createElement('div');
      card.className = 'town-item-card town-reforge-option';
      card.setAttribute('data-focusable', 'true');

      const label = document.createElement('div');
      label.className = 'town-item-name';
      label.textContent = `Option ${i + 1}`;
      card.appendChild(label);

      const affixDesc = document.createElement('div');
      affixDesc.className = 'town-item-meta';
      affixDesc.textContent = option.description || option.name || 'Unknown affix';
      card.appendChild(affixDesc);

      card.addEventListener('click', () => {
        // Replace the first affix
        const affixIndex = 0;
        const result = townSystem.reforgeItem(this._party, item.instanceId, affixIndex, option);
        this._statusMsg = result.message;
        this._selectedItem = null;
        this._reforgeOptions = [];
        this._render();
        focusManager.refreshGroup();
      });

      grid.appendChild(card);
    }

    // Cancel button
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'town-panel-btn';
    cancelBtn.setAttribute('data-focusable', 'true');
    cancelBtn.textContent = 'Cancel Reforge';
    cancelBtn.addEventListener('click', () => {
      this._selectedItem = null;
      this._reforgeOptions = [];
      this._render();
      focusManager.refreshGroup();
    });
    container.appendChild(cancelBtn);

    container.appendChild(grid);
  }
}
